/**
 * 4 · Produzione — la postazione.
 *
 * **L'unità di lavorazione è la personalizzazione, non la cassetta.** Stampa e
 * ricamo sullo stesso capo sono due lavorazioni, due foto, due timer, due
 * chiusure — e la schermata deve renderlo evidente, perché è la cosa più facile
 * da fraintendere guardando una cassetta sola.
 *
 * Il capo sta dentro un **anello di luce**: è la postazione foto, e la foto è
 * la firma di completamento (regola 20). Il tasto di chiusura resta spento
 * finché non è stata scattata.
 *
 * **E adesso è una foto vera.** Fino a oggi «Scatta la foto» alzava un flag e
 * nessuna immagine esisteva da nessuna parte: la regola 20 era una promessa
 * senza niente dietro. Il pulsante apre la stessa fotocamera del banco — un
 * componente solo, in `components/`, perché due schermate che scattano non
 * devono avere due fotocamere — con una differenza sola, che è la via d'uscita:
 * al banco la foto **si chiede** (regola 38-bis) e si può continuare senza,
 * qui **si pretende**, quindi la porta si chiama «Annulla» e la lavorazione
 * resta aperta.
 */

import { useState } from 'react';

import { Camera } from '../components/camera';
import { Modale } from '../components/modal';
import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { SCRAP_REASONS, type Department } from '@errebi/domain';

import { Spazio, Testata, Voce } from '../components/header';
import { Avviso, useFlusso } from '../flow-api';

interface Voce {
  cassetta: {
    codice: string;
    ordine: string;
    cliente: string;
    sku: string;
    colore: string;
    capi: number;
    /**
     * Il mix di taglie, quando la cassetta ce l'ha.
     *
     * Facoltativo per la stessa ragione per cui lo è nel dominio: una cassetta
     * consegnata da un banco più vecchio torna dal database senza. Dove manca,
     * la taglia del capo rovinato si scrive a mano invece di sceglierla — e la
     * schermata lo dice, invece di disegnare una fila di pulsanti vuota.
     */
    taglie?: Record<string, number>;
    rovinati: number;
    pers: string[];
    chiuse: string[];
    carrello: string | null;
  };
  urgente: boolean;
  deroga: { chi: string; quando: string; manca: string } | null;
  /**
   * `foto` porta l'indirizzo dello scatto, non un sì.
   *
   * Era un booleano, ed è la differenza fra «qualcuno ha premuto un tasto» e
   * «c'è un'immagine che si può guardare». `null` = non ancora scattata.
   */
  lavorazione: {
    foto: { id: string; indirizzo: string } | null;
    chiusa: boolean;
    partito: number | null;
  } | null;
  ms: number;
}
/** What the floor reads in the header: the two prints keep their machine code. */
const DEPARTMENT_NAMES: Record<Department, string> = {
  DTF: 'Stampa DTF',
  DTG: 'Stampa DTG',
  WEMB: 'Ricamo',
};

interface VistaProduzione {
  reparto: Department;
  turnoAperto: boolean;
  turno: string;
  coda: Voce[];
}

function minuti(ms: number) {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function Produzione({ reparto }: { reparto: Department }) {
  const { dati, caricando, ultimo, inCorso, fai } = useFlusso<VistaProduzione>(
    `produzione-${reparto}`,
    `/produzione/${reparto}`,
  );
  const [scarto, setScarto] = useState<{
    cassetta: string;
    taglie: Record<string, number>;
    taglia: string;
    causale: string;
    testo: string;
  } | null>(null);
  /** La fotocamera aperta sulla lavorazione in corso, o chiusa. */
  const [fotocamera, setFotocamera] = useState(false);

  if (caricando || !dati) return <Stato quale="caricando" dice="Apro il reparto…" />;

  const aperta = dati.coda.find((v) => v.lavorazione && !v.lavorazione.chiusa);
  const foto = aperta?.lavorazione?.foto ?? null;

  /**
   * Lo scatto parte come multipart, e la `FormData` si compone **qui**.
   *
   * Stessa scelta del banco, e per la stessa ragione: un gesto attribuito può
   * tornare 403, la sessione chiede la password e **richiama** l'azione. Una
   * `FormData` è rispedibile, ma ricostruirla a ogni tentativo costa nulla ed è
   * una regola che non si può infrangere per distrazione.
   */
  function manda(immagine: Blob) {
    if (!aperta) return;
    const modulo = new FormData();
    modulo.append('foto', immagine, 'scatto.jpg');
    modulo.append('cassetta', aperta.cassetta.codice);
    modulo.append('reparto', reparto);
    setFotocamera(false);
    fai('/produzione/foto', modulo)();
  }

  return (
    <div className="shell">
      <Testata>
        <Voce k="Reparto" v={DEPARTMENT_NAMES[reparto]} />
        <Voce k="Turno" v={dati.turno} />
        <Spazio />
        <button
          className="mod"
          disabled={inCorso}
          onClick={fai('/produzione/turno', { aperto: !dati.turnoAperto })}
        >
          {dati.turnoAperto ? 'Chiudi il turno' : 'Apri il turno'}
        </button>
        <Notifiche />
      </Testata>

      <div className="main">
        <section className="booth">
          {aperta ? (
            <>
              {/* l'anello di luce: è la postazione foto */}
              <div className="booth__stage">
                <div
                  style={{
                    width: 190,
                    height: 190,
                    borderRadius: '50%',
                    border: '10px solid var(--hi)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--panel)',
                  }}
                >
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 22 }}>
                    {aperta.cassetta.codice}
                  </span>
                </div>
              </div>

              <div className="gesto">
                <div className="gesto__top">
                  <span className="gesto__cass">{aperta.cassetta.codice}</span>
                  <span className="gesto__fill">{minuti(aperta.ms)}</span>
                  <span className="gesto__k">
                    {dati.turnoAperto ? 'timer in corso' : 'turno chiuso'}
                  </span>
                </div>
                <div className="gesto__body">
                  <div>
                    <div className="riga__cliente">{aperta.cassetta.cliente}</div>
                    <div className="riga__sku">
                      {aperta.cassetta.sku} {aperta.cassetta.colore} ·{' '}
                      {aperta.cassetta.capi - aperta.cassetta.rovinati} capi buoni
                      {aperta.cassetta.rovinati > 0 && ` (${aperta.cassetta.rovinati} rovinati)`}
                    </div>
                    {aperta.deroga && (
                      <p className="mod__hint" style={{ color: 'var(--part)' }}>
                        ⚠ parziale autorizzato da {aperta.deroga.chi}: manca {aperta.deroga.manca}
                      </p>
                    )}
                  </div>

                  <div className="mods">
                    <button
                      className="btn"
                      disabled={inCorso || foto !== null}
                      onClick={() => setFotocamera(true)}
                    >
                      {foto !== null ? '✓ foto scattata' : 'Scatta la foto'}
                    </button>
                    <button
                      className="btn btn--primario"
                      disabled={inCorso || foto === null}
                      onClick={fai('/produzione/chiudi', {
                        cassetta: aperta.cassetta.codice,
                        reparto,
                      })}
                    >
                      Chiudi lavorazione
                    </button>
                  </div>
                  {foto !== null && (
                    // La miniatura non è decorazione: è la sola prova, sullo
                    // schermo di chi ha scattato, che l'immagine è arrivata
                    // davvero e non è un rettangolo nero.
                    <img
                      src={foto.indirizzo}
                      alt="La foto della lavorazione appena chiusa"
                      style={{ maxWidth: 160, borderRadius: 6, marginTop: 8 }}
                    />
                  )}
                  {foto === null && (
                    <p className="mod__hint">
                      La foto è la firma di completamento: senza, la lavorazione non si chiude e non
                      se ne apre un’altra.
                    </p>
                  )}

                  <div className="mods mods--stampa">
                    <button
                      className="mod mod--fal"
                      disabled={inCorso}
                      onClick={() =>
                        setScarto({
                          cassetta: aperta.cassetta.codice,
                          taglie: aperta.cassetta.taglie ?? {},
                          taglia: '',
                          causale: '',
                          testo: '',
                        })
                      }
                    >
                      ✗ capo rovinato
                    </button>
                    <span className="mod__hint">
                      Non è un fallato: misura noi, non il fornitore. Serve la causale.
                    </span>
                  </div>
                </div>
              </div>

              {/* le altre personalizzazioni della stessa cassetta */}
              <div style={{ marginTop: 14 }}>
                <span className="gesto__k">Personalizzazioni di questa cassetta</span>
                <div className="cassette" style={{ marginTop: 8 }}>
                  {aperta.cassetta.pers.map((p) => (
                    <div key={p} className="cassetta">
                      <span className="cassetta__cod">{p}</span>
                      <span className="cassetta__n">
                        {aperta.cassetta.chiuse.includes(p)
                          ? 'chiusa'
                          : p === reparto
                            ? 'in corso'
                            : 'in attesa'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="booth__vuoto" style={{ margin: 'auto' }}>
              Nessuna lavorazione aperta. Prendi una cassetta dalla coda: gli ordini urgenti sono in
              cima.
            </p>
          )}
        </section>

        <section className="righe">
          <Sezione titolo="Coda del reparto">
            {dati.coda.length === 0 && <p className="mod__hint">Niente in coda.</p>}
            {dati.coda.map((v) => (
              <article
                key={v.cassetta.codice}
                className={`riga riga--${v.urgente ? 'part' : 'att'}`}
              >
                <div className="riga__cliente">
                  {v.cassetta.cliente} {v.urgente && '· urgente'}
                </div>
                <div className="riga__sku">
                  {v.cassetta.codice} · {v.cassetta.sku} {v.cassetta.colore}
                </div>
                <div className="riga__pers">
                  {v.cassetta.pers.join(' → ')} · carrello {v.cassetta.carrello ?? '—'}
                </div>
                {!v.lavorazione?.chiusa && (
                  <button
                    className="mod"
                    style={{ marginTop: 8 }}
                    disabled={inCorso || !!aperta}
                    onClick={fai('/produzione/apri', { cassetta: v.cassetta.codice, reparto })}
                  >
                    {v.lavorazione ? 'Riprendi' : 'Apri lavorazione'}
                  </button>
                )}
              </article>
            ))}
          </Sezione>
        </section>
      </div>

      {/* il gesto in due tempi: prima il capo, poi il perché */}
      {scarto && (
        <div className="velo">
          <div className="scheda">
            <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Quale capo, e perché</h2>
            <p style={{ color: 'var(--ink2)', fontSize: 14, margin: '0 0 16px' }}>
              La taglia serve a rifarlo: senza, l’avviso all’ufficio dice che manca un capo e non
              dice quale. Il perché serve a non rifarlo di nuovo — una macchina che si inceppa si
              aggiusta, un filato che si rompe si cambia, un’impostazione sbagliata si corregge a
              monte.
            </p>

            <span className="gesto__k">Taglia</span>
            {Object.keys(scarto.taglie).length > 0 ? (
              <div className="mods" style={{ margin: '6px 0 14px' }}>
                {Object.entries(scarto.taglie).map(([t, quanti]) => (
                  <button
                    key={t}
                    className="mod"
                    aria-pressed={scarto.taglia === t}
                    onClick={() => setScarto({ ...scarto, taglia: t })}
                  >
                    {t} <span className="mod__hint">×{quanti}</span>
                  </button>
                ))}
              </div>
            ) : (
              /*
                La cassetta non dichiara le sue taglie: è arrivata da un banco
                più vecchio, e il campo non c'è. Si scrive, e si dice perché —
                una fila di pulsanti vuota sembrerebbe un guasto.
              */
              <label className="campo" style={{ margin: '6px 0 14px' }}>
                <input
                  value={scarto.taglia}
                  placeholder="M"
                  onChange={(e) => setScarto({ ...scarto, taglia: e.target.value })}
                />
                <span className="mod__hint">
                  Questa cassetta non dice quali taglie contiene: scrivila a mano.
                </span>
              </label>
            )}

            <span className="gesto__k">Perché si è rovinato</span>
            <div style={{ height: 6 }} />
            {SCRAP_REASONS.map((c) => (
              <button
                key={c.k}
                className="mod"
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 6 }}
                aria-pressed={scarto.causale === c.k}
                onClick={() => setScarto({ ...scarto, causale: c.k })}
              >
                {c.nome} <span className="mod__hint">— {c.nota}</span>
              </button>
            ))}
            {scarto.causale === 'altro' && (
              <label className="campo" style={{ marginTop: 10 }}>
                <span className="campo__k">Cos’è successo</span>
                <input
                  value={scarto.testo}
                  onChange={(e) => setScarto({ ...scarto, testo: e.target.value })}
                  autoFocus
                />
              </label>
            )}
            <div className="mods" style={{ marginTop: 12 }}>
              <button
                className="btn btn--primario"
                disabled={inCorso || !scarto.causale || !scarto.taglia.trim()}
                onClick={() => {
                  fai('/produzione/rovinato', {
                    cassetta: scarto.cassetta,
                    reparto,
                    taglia: scarto.taglia.trim(),
                    capi: 1,
                    causale: scarto.causale,
                    spiegazione: scarto.testo || null,
                  })();
                  setScarto(null);
                }}
              >
                Registra lo scarto
              </button>
              <button className="mod" onClick={() => setScarto(null)}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {fotocamera && aperta && (
        <Modale etichetta="Fotografa la lavorazione" onChiudi={() => setFotocamera(false)}>
          <Camera
            onScatto={manda}
            onChiudi={() => setFotocamera(false)}
            soggetto={`${aperta.cassetta.codice} · ${aperta.cassetta.sku} ${aperta.cassetta.colore} · ${reparto}`}
            // Non «Continua senza foto»: qui la regola 20 la pretende, e
            // uscire lascia la lavorazione aperta com'era.
            uscita="Annulla"
            inCorso={inCorso}
          />
        </Modale>
      )}

      <Avviso esito={ultimo} />
    </div>
  );
}
